import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  AppointmentInfo,
  DoubleModal,
  EVENT_FORM,
  useModalForm,
} from '../../common'
import { useSchemaEvent } from './schema'
import { useModalContext } from '../../provider'
import { parseCustomDate, useRouteState } from '@shared/utils'
import { CareForm } from './care-form'
import { CareAppointmentState } from './state'
import { useLayoutContext } from '@sr/routes/common/layout-provider'
import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { WarningAltFilled, CheckmarkFilled } from '@carbon/icons-react'
import { DS_TYPOGRAPHY_BODY_SM_REGULAR } from '@shared/ui'
import { Button, InputTextField } from '@shared/storybook'
import { ROUTE_PATH } from 'apps/social-referent/src/routes/enums'
import { APPOINTMENT_TYPES } from '@shared/data-access'
import { SRModal, SRSuccessModal } from '@sr/ui'
import { NotifyChangesModal } from './notify-changes-modal'

export const Care = observer(
  ({ isReadOnly, isEdit, actionAfterSubmit, isCoordinator, onClose  }) => {
    const { t } = useTranslation()
    const { id: socialReferentId } = useParams()
    const { setIsLoading } = useLayoutContext()
    const {
      eventId,
      appointmentType,
      appointmentDay,
      startTime,
      endTime,
      policyId,
      modality,
      needsTracking,
      onDeleteAppointmentCallback,
      showDeletedOk,
      socialReferentId: modalSocialReferentId,
    } = useModalContext()
    const {
      isLoading,
      policyClients,
      canGenerateRoom,
      saveAppointment,
      createAppointment,
      setAppointmentType,
      isOwner,
      setSearch,
      policy,
      deleteCsAppointment,
      availableSocialReferentOptions,
    } = useRouteState(CareAppointmentState, {
      eventId,
      isEdit,
      socialReferentId,
      appointmentType,
      policyId,
    })
    const [isDeleting, setIsDeleting] = useState(false)
    const [inputDeleteValue, setInputDeleteValue] = useState("")
    const [disabledDelete, setDisabledDelete] = useState(true)
    const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false)
    const [pendingForm, setPendingForm] = useState(null)
    const [defaultNotifyClient, setDefaultNotifyClient] = useState(true)
    const onChange = (e) => {
      setSearch(e.target.value)
    }
    const isSuscriptionControl =
    appointmentType === APPOINTMENT_TYPES.INITIAL_REPORT ||
    appointmentType === APPOINTMENT_TYPES.INITIAL_REPORT_FACE_TO_FACE
    const schema = useSchemaEvent(
      t,
      isEdit,
      needsTracking,
      isSuscriptionControl,
    )

    const preferredSocialReferentId =
      modalSocialReferentId ||
      policy?.socialReferent?.id ||
      socialReferentId ||
      ''

    const defaultValues = {
      [EVENT_FORM.appointmentType]: appointmentType,
      [EVENT_FORM.appointmentDay]: appointmentDay,
      [EVENT_FORM.appointmentPolicy]: policyId || '',
      [EVENT_FORM.startDate]: startTime,
      [EVENT_FORM.endDate]: endTime,
      [EVENT_FORM.appointmentModality]: modality,
      [EVENT_FORM.appointmentSocialReferent]: preferredSocialReferentId,
    }

    const { methods } = useModalForm({ schema, defaultValues, isEdit })

    const { handleSubmit, setValue } = methods

    const executeSubmit = async (form, shouldNotifyClient = true) => {
      setSuccessOpen(false)
      setErrorOpen(false)
      setErrorMessage('')
      setHideForm(false)
      setIsSubmitting(true)
      const {
        appointmentType,
        appointmentPolicy,
        appointmentModality,
        appointmentModificationOrigin,
        appointmentModificationReason,
        appointmentSocialReferent,
      } = form
      const { startDate, endDate } = form
      const newStartDate = parseCustomDate(appointmentDay, startDate)
      const newEndDate = parseCustomDate(appointmentDay, endDate)

      // Convertir INITIAL_REPORT_FACE_TO_FACE (555) a formato del backend
      let backendAppointmentType = appointmentType;
      let backendAppointmentModality = Number(appointmentModality);

      // SOLO convertir modalidad a 0 si el tipo original era 555 Y NO se cambió manualmente
      if (appointmentType === 555) { // INITIAL_REPORT_FACE_TO_FACE
        backendAppointmentType = 0; // INITIAL_REPORT
        // NO forzar appointmentModality aquí - usar el valor del formulario
        // backendAppointmentModality ya tiene el valor correcto del formulario
      }

      const eventForm = {
        startDate: newStartDate,
        endDate: newEndDate,
        appointmentType: backendAppointmentType,
        id: eventId || '',
        policyId: appointmentPolicy || policyId,
        socialReferentId:
          appointmentSocialReferent ||
          policy?.socialReferent?.id ||
          socialReferentId,
        appointmentModality: backendAppointmentModality,
        // Siempre incluir modification origin y reason para ediciones
        ...(isEdit && {
          appointmentModificationReason: appointmentModificationReason || 5,
          appointmentModificationOrigin: appointmentModificationOrigin || 2,
          notifyClient: shouldNotifyClient,
        }),
      }
      console.log('eventForm', eventForm)
      const action = isEdit ? saveAppointment : createAppointment
      try {
        const result = await action(eventForm)
        const status = result?.status || result?.response?.status || null
        const hasErrorStatus = status && Number(status) >= 400

        if (result === false || hasErrorStatus) {
          setErrorMessage( t('agenda.formModal.genericErrorMessage'))
          setErrorOpen(true)
          setHideForm(false)
          return
        }

        setHideForm(true)
        setSuccessOpen(true)
      } catch (error) {
        console.error('❌ Error guardando la cita:', error)
        const fallbackMessage = t('agenda.formModal.genericErrorMessage')
        setErrorMessage( fallbackMessage)
        setErrorOpen(true)
        setHideForm(false)
      } finally {
        setIsSubmitting(false)
      }
    }

    const onSubmit = handleSubmit(async (form) => {
      if (isEdit) {
        setPendingForm(form)
        setDefaultNotifyClient(true)
        setIsNotifyModalOpen(true)
        return
      }

      await executeSubmit(form, true)
    })

    const [successOpen, setSuccessOpen] = useState(false)
    const [errorOpen, setErrorOpen] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [hideForm, setHideForm] = useState(false)

    const [isEditing, setIsEditing] = useState(false)
    const toggleEdit = () => {
      setIsEditing(!isEditing)
    }

    useEffect(() => {
      setIsLoading(isLoading)
    }, [isLoading])

    useEffect(() => {
      setAppointmentType(appointmentType)
    }, [appointmentType])

    useEffect(() => {
      if (!isSuscriptionControl) {
        return
      }

      if (preferredSocialReferentId) {
        setValue(EVENT_FORM.appointmentSocialReferent, preferredSocialReferentId)
      }
    }, [
      preferredSocialReferentId,
      isSuscriptionControl,
      setValue,
    ])

    const showResendComms = Boolean(
      appointmentType === APPOINTMENT_TYPES.INITIAL_ANALYSIS ||
      appointmentType === APPOINTMENT_TYPES.INITIAL_REPORT ||
      appointmentType === APPOINTMENT_TYPES.INITIAL_REPORT_FACE_TO_FACE ||
      appointmentType === APPOINTMENT_TYPES.FOLLOW_UP_ANALYSIS ||
      appointmentType === APPOINTMENT_TYPES.FOLLOW_UP_ANALYSIS_CALL,
    )

    const onClickDeleteAppointment = async () => {
      onClose()
      await deleteCsAppointment(eventId)
      await onDeleteAppointmentCallback()
    }

    const onDeleteClose = () => {
      setInputDeleteValue("")
      setDisabledDelete(true)
      setIsDeleting(false)
    }

    if (showDeletedOk) {
      return (
        <SRModal
          onClose={() => onClose()}
          title={t('agenda.formModal.DeleteSuccessTitle')}
          className="max-w-[520px]"
        >
          <div className="relative">
            <CheckmarkFilled size={48} className="m-auto mt-10 mb-10" color="#0E8343" />
            <div className={`font-aeonik text-lg leading-6`}>{t('agenda.formModal.DeleteSuccessText')}</div>
          </div>
          <div className="flex justify-end mt-8">
            <Button
              size="lg"
              className="ml-4"
              onClick={() => onClose()}
            >
              {t('agenda.formModal.deleteSuccessButton')}
            </Button>
          </div>
        </SRModal>
      )
    }

    const closeNotifyModal = () => {
      setIsNotifyModalOpen(false)
      setPendingForm(null)
    }

    const handleNotifyConfirm = async (shouldNotifyClient) => {
      setIsNotifyModalOpen(false)
      if (pendingForm) {
        await executeSubmit(pendingForm, shouldNotifyClient)
        setPendingForm(null)
      }
    }

    if (isDeleting) {
      const onInputChange = (value) => {
        setInputDeleteValue(value)
        setDisabledDelete(value !== "ELIMINAR")
      }

      return (
        <SRModal
          onClose={() => setIsDeleting(false)}
          title={t('agenda.formModal.DeleteTitle')}
          className="max-w-[520px]"
        >
          <div className="relative">
            <WarningAltFilled size={48} className="m-auto mt-10 mb-10" color="#AD941A" />
            <div className={`font-aeonik text-lg leading-6`}>{t('agenda.formModal.DeleteText')} <b>{t('agenda.formModal.DeleteTextBold')}</b></div>
            <div className={`${DS_TYPOGRAPHY_BODY_SM_REGULAR} mt-8`}>{t('agenda.formModal.DeleteTextConfirmation')}</div>
            <InputTextField className="mt-2" placeholder="" value={inputDeleteValue} onChange={onInputChange} />
          </div>
          <div className="flex justify-end mt-8">
            <Button
              variant="secondary"
              size="lg"
              onClick={onDeleteClose}
            >
              {t('common:cancel')}
            </Button>
            <Button
              size="lg"
              className="ml-4"
              onClick={() => onClickDeleteAppointment()}
              disabled={disabledDelete}
            >
              {t('agenda.formModal.deleteButton')}
            </Button>
          </div>
        </SRModal>
      )
    }

    if (isSubmitting) {
      return (
        <SRModal onClose={() => {}} className="max-w-[400px]">
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
            <div className="text-base">{t('common:loading') || 'Cargando...'}</div>
          </div>
        </SRModal>
      )
    }

    if (successOpen) {
      return (
        <SRSuccessModal
          mainMessage={t('agenda.formModal.successTitle')}
          secondaryMessage={t('agenda.formModal.successText')}
          noRoute
          onClose={() => {
            setSuccessOpen(false)
            actionAfterSubmit()
          }}
        />
      )
    }

    if (errorOpen) {
      return (
        <SRModal
          onClose={() => setErrorOpen(false)}
          title={t('agenda.formModal.genericErrorTitle')}
          className="max-w-[520px]"
        >
          <div className="text-base">{errorMessage || t('agenda.formModal.genericErrorMessage')}</div>
          <div className="flex justify-end mt-6">
            <Button
              size="lg"
              onClick={() => setErrorOpen(false)}
            >
              {t('common:ok') || 'OK'}
            </Button>
          </div>
        </SRModal>
      )
    }

    if (hideForm) {
      return null
    }

    return (
      <>
        <DoubleModal
          isSuscriptionControl={isSuscriptionControl}
          onClose={onClose}
          isEdit={isEdit}
          isReadOnly={isReadOnly}
          methods={methods}
          toggleEdit={toggleEdit}
          isEditing={isEditing}
          appointmentId={eventId}
          formSlot={
            <CareForm
              isCoordinator={isCoordinator}
              isReadOnly={isReadOnly}
              isEdit={isEdit}
              policyClients={policyClients}
              isEditing={isEditing}
              needsTracking={needsTracking}
              searchSelectProps={{ onChange }}
              officeStreet={policy?.completeOfficeAddress}
              showSocialReferentSelect={isSuscriptionControl}
              socialReferentOptions={availableSocialReferentOptions}
            />
          }
          infoSlot={
            <AppointmentInfo
              canGenerateRoom={canGenerateRoom?.result}
              profileUrl={ROUTE_PATH.userArea(policyId)}
              isCoordinator={isCoordinator}
              isOwner={isOwner}
              isEditing={isEditing}
              showResendComms={showResendComms}
            />
          }
          onSubmit={onSubmit}
          onClickDeleteAppointment={() => setIsDeleting(true)}
        />
        <NotifyChangesModal
          open={isNotifyModalOpen}
          defaultChecked={defaultNotifyClient}
          onConfirm={handleNotifyConfirm}
          onClose={closeNotifyModal}
          isLoading={isSubmitting}
        />
      </>
    )
  },
)